import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectCategoryProductFilter } from 'app/pages/Home/slice/selectors';
import { MomentData, MilestonesGridData } from '../types';

/**
 * Custom hook to filter milestones grid data by Categories and Products
 * Uses OR logic: if both category and product filters are selected, show items that match EITHER
 * Filters on: moment.filters.categories (array) and moment.filters.products (array)
 */
export const useFilterByCategoryProduct = (
  milestonesData: MilestonesGridData | null
): MilestonesGridData | null => {
  // Get selected categories and products from Redux state
  const categoryProductFilter = useSelector(selectCategoryProductFilter);
  const selectedCategories = categoryProductFilter?.selectedCategories || [];
  const selectedProducts = categoryProductFilter?.selectedProducts || [];

  // Memoize the filtered data to avoid unnecessary recalculations
  const filteredData = useMemo(() => {
    // If no data, return null
    if (!milestonesData || !milestonesData.data) {
      return null;
    }

    // If no category/product filters are selected, return original data
    if (selectedCategories.length === 0 && selectedProducts.length === 0) {
      return milestonesData;
    }

    // Filter the data based on selected categories and products
    const filtered: MomentData[] = milestonesData.data.filter(moment => {
      const hasCategoryFilter = selectedCategories.length > 0;
      const hasProductFilter = selectedProducts.length > 0;

      // Category Filter Check (exact matching)
      let categoryMatches = false;
      if (hasCategoryFilter) {
        const momentCategories = moment.filters?.categories;
        if (
          momentCategories &&
          Array.isArray(momentCategories) &&
          momentCategories.length > 0
        ) {
          // Check if any of the moment's categories matches any selected category (exact match)
          categoryMatches = momentCategories.some(category =>
            selectedCategories.includes(category)
          );
        }
      }

      // Product Filter Check (exact matching)
      let productMatches = false;
      if (hasProductFilter) {
        const momentProducts = moment.filters?.products;
        if (
          momentProducts &&
          Array.isArray(momentProducts) &&
          momentProducts.length > 0
        ) {
          // Check if any of the moment's products matches any selected product (exact match)
          productMatches = momentProducts.some(product =>
            selectedProducts.includes(product)
          );
        }
      }

      // OR logic: show items that match category OR product (or both)
      // If only category selected: return categoryMatches
      // If only product selected: return productMatches
      // If both selected: return categoryMatches OR productMatches
      if (hasCategoryFilter && hasProductFilter) {
        return categoryMatches || productMatches;
      } else if (hasCategoryFilter) {
        return categoryMatches;
      } else if (hasProductFilter) {
        return productMatches;
      }

      return false; // Should never reach here due to early return above
    });

    return {
      ...milestonesData,
      data: filtered
    };
  }, [milestonesData, selectedCategories, selectedProducts]);

  return filteredData;
};
